import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useProfile } from '../hooks/useAuth.js';

interface Props {
  children: ReactNode;
}

/**
 * Wraps protected routes. Redirects to /login if the API returns 401.
 * Redirects to /onboarding if the user hasn't completed setup yet,
 * unless they're already on /onboarding (accessible any time).
 */
export function AuthGuard({ children }: Props) {
  const { isLoading, isError, data: profile } = useProfile();
  const { pathname } = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-gray-500 dark:text-gray-400 text-sm">Loading…</div>
      </div>
    );
  }

  if (isError) {
    return <Navigate to="/login" replace />;
  }

  // First-time users are sent to onboarding (wizard is also reachable any time later)
  if (profile && !profile.onboardingComplete && pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
